import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDateFormat } from '@/i18n/dateFormat';
import {
  getUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from './api';
import type { NotificationView } from './types';

function getStringField(data: Record<string, unknown> | null | undefined, key: string): string | null {
  const v = data?.[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function notificationLink(n: NotificationView): string | null {
  if (n.category === 'MESSAGE_NEW' || n.category === 'MESSAGE_THREAD_ASSIGNED') {
    const projectId = getStringField(n.data, 'projectId');
    const threadId = getStringField(n.data, 'threadId');
    if (projectId && threadId) {
      return `/projects/${projectId}?tab=messages&thread=${threadId}`;
    }
  }
  if (n.category === 'PROJECT_MEMBER_ADDED') {
    const projectId = getStringField(n.data, 'projectId');
    if (projectId) return `/projects/${projectId}`;
  }
  return null;
}

export function NotificationBell(): JSX.Element {
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  const queryClient = useQueryClient();

  const countQ = useQuery({
    queryKey: ['notif-unread'],
    queryFn: getUnreadCount,
    refetchInterval: 30_000,
  });

  const listQ = useQuery({
    queryKey: ['notif-recent'],
    queryFn: () => listNotifications({ page: 0, size: 10 }),
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notif-unread'] });
      void queryClient.invalidateQueries({ queryKey: ['notif-recent'] });
    },
  });

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notif-unread'] });
      void queryClient.invalidateQueries({ queryKey: ['notif-recent'] });
    },
  });

  return (
    <>
      <IconButton color="inherit" onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={t('notifications.title')}>
        <Badge badgeContent={countQ.data ?? 0} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 360, maxHeight: 480 } } }}
      >
        <Box sx={{ p: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" fontWeight={600}>
              {t('notifications.title')}
            </Typography>
            <Button size="small" onClick={() => markAll.mutate()} disabled={(countQ.data ?? 0) === 0}>
              {t('notifications.bell.markAllRead')}
            </Button>
          </Stack>
        </Box>
        <Divider />
        {listQ.isLoading && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <CircularProgress size={22} />
          </Box>
        )}
        {!listQ.isLoading && (listQ.data?.content.length ?? 0) === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('notifications.bell.empty')}
            </Typography>
          </Box>
        )}
        <List dense disablePadding>
          {listQ.data?.content.map((n) => (
            <ListItemButton
              key={n.id}
              onClick={() => {
                if (!n.readAt) markRead.mutate(n.id);
                const link = notificationLink(n);
                if (link) {
                  setAnchor(null);
                  navigate(link);
                }
              }}
              sx={{ bgcolor: n.readAt ? 'transparent' : 'action.hover' }}
            >
              <ListItemText
                primary={n.title}
                secondary={
                  <>
                    <Typography variant="caption" color="text.secondary">
                      {t(`notifications.categories.${n.category}`)} · {formatDateTime(n.createdAt)}
                    </Typography>
                    <Typography variant="body2">{n.body}</Typography>
                  </>
                }
                secondaryTypographyProps={{ component: 'div' }}
              />
            </ListItemButton>
          ))}
        </List>
        <Divider />
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button
            component={RouterLink}
            to="/notifications/preferences"
            size="small"
            onClick={() => setAnchor(null)}
          >
            {t('nav.notificationPrefs')}
          </Button>
        </Box>
      </Popover>
    </>
  );
}
